import { useEffect, useRef, useState } from 'react';
import { Check, Search } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { cn } from '@Utils';

import { ChainAvatar } from '@Components/ConnectedChainCard';
import Modal from '@Components/ui/modal';
import Spinner from '@Components/ui/spinner';
import { Button } from '@Components/ui-v2/button';
import { Empty, EmptyHeader, EmptyTitle } from '@Components/ui-v2/empty';
import { Input } from '@Components/ui-v2/input';
import { Pager } from '@Components/ui-v2/pager';
import { Skeleton } from '@Components/ui-v2/skeleton';
import { useDebounce, useStores } from '@CustomHooks';
import { getRetailerColor, getRetailerName, getRetailerShort } from '../constants';

/** fastapi-pagination caps `size` at 100. */
const PAGE_SIZE = 100;
const SEARCH_DEBOUNCE_MS = 500;

interface CoverageStoresModalProps {
  retailerId: string;
  /** location_codes present in field_service_coverage for this contract. */
  coveredCodes: Set<string>;
  /** Active stores for this retailer, from the detail response. */
  totalStores: number;
  onClose: () => void;
}

/** Read only: coverage has no write endpoint, so rows report state and never set it. */
const CoverageStoresModal = ({ retailerId, coveredCodes, totalStores, onClose }: CoverageStoresModalProps) => {
  const { t } = useTranslation();
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounce(search, SEARCH_DEBOUNCE_MS);
  const [pageState, setPageState] = useState({ search: '', page: 1 });
  const page = pageState.search === debouncedSearch ? pageState.page : 1;
  const handlePageChange = (nextPage: number) => setPageState({ search: debouncedSearch, page: nextPage });

  const { data, isPending, isError } = useStores({
    retailer: retailerId,
    search: debouncedSearch,
    page,
    size: PAGE_SIZE,
  });

  // Keyed on data rather than page: the previous page stays on screen until the new one arrives.
  const listRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (listRef.current) listRef.current.scrollTop = 0;
  }, [data]);

  const stores = data?.items ?? [];
  const matched = data?.total ?? 0;
  const pageCount = data?.pages ?? 1;

  const countLabel = t('FieldService.Coverage.ModalCount', {
    covered: coveredCodes.size,
    matched,
    total: totalStores,
  });

  return (
    <Modal modalOpen closeModal={onClose} className="max-w-lg items-stretch justify-start gap-4 p-0">
      {/* The overlay sets cursor-pointer so clicking outside closes. Each non-interactive block
          opts back out, rather than the container doing it and stealing it from the close X. */}
      <div className="flex cursor-default items-center gap-3 border-b border-gray-100 px-5 py-4 pr-12">
        <ChainAvatar
          size="sm"
          initials={getRetailerShort(retailerId)}
          color={getRetailerColor(retailerId)}
          alt={getRetailerName(retailerId)}
        />
        <div className="min-w-0">
          <div className="truncate text-[15px] font-bold text-gray-900">
            {t('FieldService.Coverage.ModalTitlePrefix')} · {getRetailerName(retailerId)}
          </div>
          <div className="text-xs text-gray-500">{t('FieldService.Coverage.ModalSubtitle')}</div>
        </div>
      </div>

      <div className="flex flex-col gap-3">
        <div className="relative px-5">
          <Search
            size={16}
            aria-hidden="true"
            className="pointer-events-none absolute left-8 top-1/2 -translate-y-1/2 text-gray-400"
          />
          <Input
            type="text"
            // A placeholder is not a reliable accessible name, and it disappears on typing.
            aria-label={t('FieldService.Coverage.SearchPlaceholder')}
            placeholder={t('FieldService.Coverage.SearchPlaceholder')}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full cursor-text pl-9 text-xs md:text-xs"
          />
        </div>
        {/* Full bleed so the scrollbar sits on the modal edge; the rows carry the padding.
            Scrollbar mirrors the design: a 10px channel whose thumb is inset 2px a side by a
            transparent border, reading as 6px with air around it. Chromium only honours the
            sub-part rules once ::-webkit-scrollbar itself is styled. */}
        <div
          ref={listRef}
          className="max-h-72 w-full cursor-default overflow-y-auto [&::-webkit-scrollbar-thumb:hover]:bg-[#D0D5DD] [&::-webkit-scrollbar-thumb]:rounded-md [&::-webkit-scrollbar-thumb]:border-2 [&::-webkit-scrollbar-thumb]:border-transparent [&::-webkit-scrollbar-thumb]:border-solid [&::-webkit-scrollbar-thumb]:bg-[#E4E7EC] [&::-webkit-scrollbar-thumb]:bg-clip-content [&::-webkit-scrollbar-track]:bg-transparent [&::-webkit-scrollbar]:w-2.5">
          {isPending && <Spinner className="my-8" />}

          {!isPending && isError && (
            <p className="py-6 text-center text-sm text-red-500">{t('FieldService.Coverage.LoadError')}</p>
          )}

          {!isPending && !isError && stores.length === 0 && (
            <Empty className="border-0 p-6">
              <EmptyHeader>
                <EmptyTitle className="text-sm font-normal text-gray-500">
                  {t('FieldService.Coverage.NoMatches')}
                </EmptyTitle>
              </EmptyHeader>
            </Empty>
          )}

          {!isPending &&
            !isError &&
            stores.map((store) => {
              const isCovered = coveredCodes.has(store.location_code);
              return (
                <div
                  key={store.location_code}
                  role="checkbox"
                  aria-checked={isCovered}
                  aria-disabled="true"
                  className={cn(
                    'flex w-full items-center gap-2.5 border-b border-gray-100 px-5 py-2.5 text-left text-[13px] text-gray-700 last:border-0',
                    // Covered rows deepen their own tint instead of going grey, so the
                    // coverage stays readable while the cursor travels the list.
                    isCovered ? 'bg-brand-50 hover:bg-brand-100' : 'hover:bg-gray-50',
                  )}>
                  <span
                    className={cn(
                      'flex h-4 w-4 flex-shrink-0 items-center justify-center rounded border',
                      isCovered ? 'border-brand-600 bg-brand-600' : 'border-gray-300 bg-white',
                    )}
                    aria-hidden="true">
                    {isCovered && <Check size={11} className="text-white" strokeWidth={3} />}
                  </span>
                  <div className="min-w-0 flex-1">
                    <div className="truncate font-semibold">{store.name || store.location_code}</div>
                  </div>
                </div>
              );
            })}
        </div>
      </div>

      <div className="flex items-center justify-between gap-3 border-t border-gray-100 px-5 py-3">
        {/* The counts need the first page to exist, so a placeholder holds the row height
            instead of briefly reading "0 of 0". */}
        {isPending ? (
          <Skeleton className="h-3.5 w-44" />
        ) : (
          <span className="cursor-default text-xs tabular-nums text-gray-500">{countLabel}</span>
        )}
        <div className="flex items-center gap-2">
          {pageCount > 1 && (
            <Pager
              page={page}
              pageCount={pageCount}
              onPageChange={handlePageChange}
              ariaLabel={t('FieldService.Coverage.PagerAriaLabel')}
              previousLabel={t('FieldService.Coverage.PreviousPage')}
              nextLabel={t('FieldService.Coverage.NextPage')}
            />
          )}
          <Button
            size="sm"
            onClick={onClose}
            className="h-8 bg-brand-600 text-xs font-semibold text-white hover:bg-brand-700">
            {t('FieldService.Coverage.CloseModal')}
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default CoverageStoresModal;
